<template>
  <section class="modal-card-body">
    <form @submit.prevent="preSubmit">
      <div class="field">
        <label class="label">Patient</label>
        <div v-if="edit" class="control">
          <input
            type="text"
            disabled
            class="input"
            :value="encounter.patient.name"
          />
        </div>
        <multiselect
          v-if="!edit"
          v-model="encounter.patient"
          :options="patients"
          :multiple="false"
          :searchable="true"
          :loading="patientLoading"
          @search-change="asyncPatientFind"
          placeholder="Type to search"
          selectLabel=""
          label="name"
          track-by="id"
        >
          Oops! We cannot find that patient. Consider changing the search query.
        </multiselect>
        <p v-if="errors.patient" class="help is-danger">Patient is required</p>
      </div>

      <div class="columns">
        <div class="column">
          <div class="field">
            <label class="label">Date</label>
            <div class="control has-icon-right">
              <flat-pickr
                v-model="encounter.date"
                :config="dateConfig"
                class="input"
              ></flat-pickr>
              <p v-if="errors.date" class="help is-danger">Date is required</p>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">Time</label>
            <div class="control has-icon-right">
              <flat-pickr
                v-model="encounter.time"
                :config="timeConfig"
                class="input"
              ></flat-pickr>
              <p v-if="errors.time" class="help is-danger">Time is required</p>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">Duration</label>
            <div class="control select is-fullwidth">
              <select name="duration" v-model="encounter.duration">
                <option value="15">15 min</option>
                <option value="30">30 min</option>
                <option value="45">45 min</option>
                <option value="60">1 hour</option>
                <option value="75">1:15</option>
                <option value="90">1:30</option>
                <option value="105">1:45</option>
                <option value="120">2:00</option>
                <option value="135">2:15</option>
                <option value="150">2:30</option>
                <option value="165">2:45</option>
                <option value="180">3:00</option>
              </select>
              <p v-if="errors.duration" class="help is-danger">
                Duration is required
              </p>
            </div>
          </div>
        </div>
        <div class="column is-1">
          <button
            type="button"
            @click="meetNow"
            class="button is-small is-primary is-outlined"
            style="margin-top:2.5rem"
          >
            now
          </button>
        </div>
      </div>

      <div class="field">
        <label class="label">Provider</label>
        <div class="control">
          <multiselect
            v-model="encounter.provider"
            :options="providers"
            :multiple="false"
            :searchable="true"
            placeholder="Type to search"
            selectLabel=""
            label="name"
            track-by="id"
          >
            Oops! We cannot find any providers. Please contact an admin.
          </multiselect>
          <p v-if="errors.provider" class="help is-danger">
            Provider is required
          </p>
        </div>
      </div>

      <div class="field">
        <label class="label">Reason for Visit</label>
        <input type="text" v-model="encounter.reasonForVisit" class="input" />
        <p v-if="errors.reason" class="help is-danger">Reasons are required</p>
      </div>

      <div v-if="showDelete" class="field is-pulled-left">
        <button
          type="button"
          :class="['button', 'is-danger', { 'is-loading': deleteLoading }]"
          @click="$emit('delete')"
        >
          Delete
        </button>
      </div>
      <div class="field is-grouped is-pulled-right">
        <div class="control">
          <button :class="['button', 'is-primary', { 'is-loading': loading }]">
            {{ label }}
          </button>
        </div>
        <div class="control">
          <a class="button" @click="$emit('hide')">Cancel</a>
        </div>
      </div>
    </form>

    <vue-toastr ref="toastr"></vue-toastr>
  </section>
</template>

<script>
/* eslint func-names: ["error", "never"] */

import _ from "lodash";
import moment from "moment";
import multiselect from "vue-multiselect";
import flatPickr from "vue-flatpickr-component";
import PatientsService from "@/services/PatientsService";
import ProvidersService from "@/services/ProvidersService";
import SymptomsService from "@/services/SymptomsService";
import "../../../node_modules/flatpickr/dist/flatpickr.css";
import ReasonSelector from "./ReasonSelector";

export default {
  name: "schedule-form",

  props: [
    "encounter",
    "label",
    "onSubmit",
    "showDelete",
    "deleteLoading",
    "edit"
  ],

  components: {
    multiselect,
    flatPickr,
    ReasonSelector
  },

  data() {
    return {
      loading: false,
      errors: {
        patient: false,
        date: false,
        time: false,
        duration: false,
        provider: false,
        reason: false
      },
      dateConfig: {
        inline: false,
        dateFormat: "m/d/Y",
        minDate: "today"
      },
      timeConfig: {
        enableTime: true,
        noCalendar: true,
        enableSeconds: false, // disabled by default
        time_24hr: false, // AM/PM time picker is used by default
        dateFormat: "h:i K",
        minuteIncrement: 15
      },
      patients: [],
      patientLoading: false,
      providers: []
    };
  },

  computed: {
    show() {
      return this.$store.getters["encounter/showScheduler"];
    },
    symptoms() {
      return this.$store.getters["symptom/list"];
    }
  },

  methods: {
    asyncPatientFind: _.debounce(function(query) {
      this.patientLoading = true;
      if (query.length) {
        PatientsService.search(query).then(results => {
          if (_.size(results)) {
            this.patients = Object.values(results).map(patient => {
              const name = `${patient.attributes.firstName} ${
                patient.attributes.lastName
              }`;
              const dob = patient.attributes.birthday;
              const phone = patient.attributes.phoneNumber;
              return {
                id: patient.id,
                name: `${name}, ${dob}, ${phone}`
              };
            });
          } else {
            this.patients = [];
          }
        });
      }
      this.patientLoading = false;
    }, 500),

    preSubmit() {
      this.loading = true;

      let allFields = true;
      [
        "patient",
        "date",
        "time",
        "duration",
        "provider",
        "reasonForVisit"
      ].forEach(field => {
        if (!this.encounter[field]) {
          allFields = false;
          this.errors[field] = true;
        } else {
          this.errors[field] = false;
        }
      });

      if (!allFields) {
        this.$refs.toastr.e("All fields are required.");
        this.loading = false;
      } else {
        const formValues = _.cloneDeep(this.encounter);
        // combine date & time
        formValues.appointmentAt = moment(
          `${formValues.date} ${formValues.time}`,
          "MM/DD/YYYY h:m a",
          true
        ).format("YYYY-MM-DD HH:mm:ss");
        delete formValues.date;
        delete formValues.time;
        // patient
        formValues.patientId = formValues.patient.id;
        delete formValues.patient;
        // provider
        formValues.providerId = formValues.provider.id;
        delete formValues.provider;

        this.onSubmit(formValues);
        this.loading = false;
      }
    },
    meetNow() {
      this.encounter.date = moment().format("MM/DD/Y");
      this.encounter.time = moment().format("HH:mm");
    }
  },

  mounted() {
    ProvidersService.load().then(results => {
      this.providers = Object.values(results).map(provider => ({
        id: provider.id,
        name: `${provider.attributes.firstName} ${provider.attributes.lastName}`
      }));
    });

    SymptomsService.load();
  }
};
</script>

<style lang="scss" scoped></style>
